import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "@/shared/icons";
import { useBreadcrumbContext } from "./BreadcrumbContext";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SEGMENT_LABELS: Record<string, { plural: string; singular: string }> = {
  dashboard: { plural: "Dashboard", singular: "Dashboard" },
  users: { plural: "Users", singular: "User" },
  departments: { plural: "Departments", singular: "Department" },
  clients: { plural: "Clients", singular: "Client" },
  templates: { plural: "Templates", singular: "Template" },
  projects: { plural: "Projects", singular: "Project" },
  "work-units": { plural: "Work Units", singular: "Work Unit" },
  documents: { plural: "Documents", singular: "Document" },
  approvals: { plural: "Approvals", singular: "Approval" },
  "live-monitor": { plural: "Live Monitor", singular: "Live Monitor" },
  payroll: { plural: "Payroll", singular: "Payroll" },
  runs: { plural: "Monthly Payroll", singular: "Payroll Month" },
  "cash-pools": { plural: "Cash Pool", singular: "Cash Pool Month" },
  lines: { plural: "Salary Breakdown", singular: "Salary Breakdown" },
  employees: { plural: "Employees", singular: "Employee" },
  payments: { plural: "Payments", singular: "Payment" },
  arrears: { plural: "Recalculations", singular: "Recalculation" },
  guide: { plural: "Help & Guide", singular: "Help & Guide" },
  mail: { plural: "Mail", singular: "Mail" },
  inbox: { plural: "Inbox", singular: "Inbox" },
  mailboxes: { plural: "Mailboxes", singular: "Mailbox" },
  rules: { plural: "Rules", singular: "Rule" },
};

// Path-prefixed segments that have no list page of their own. Rendered as
// plain text (not a link) so a click doesn't fall through to the wildcard
// route → home redirect. Keyed by `parent/seg` so we only mark "users"
// non-navigable when nested under e.g. /live-monitor/users/:id.
const NON_NAVIGABLE_PATHS = new Set<string>([
  "live-monitor/users",
]);

const ACTION_LABELS: Record<string, string> = {
  new: "New",
  edit: "Edit",
};

function titleCase(s: string): string {
  return s
    .split("-")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

function resolveLabel(
  parts: string[],
  idx: number,
  override: { status: "loading" | "ready" | "notfound"; label: string | null } | undefined,
): string {
  const seg = parts[idx];
  if (!seg) return "";

  if (UUID_RE.test(seg)) {
    if (!override || override.status === "loading") return "…";
    if (override.status === "notfound") return "Not found";
    return override.label ?? "…";
  }

  const action = ACTION_LABELS[seg];
  if (action) {
    const parentSeg = parts[idx - 1];
    const parent = parentSeg ? SEGMENT_LABELS[parentSeg] : undefined;
    return parent ? `${action} ${parent.singular}` : action;
  }

  return SEGMENT_LABELS[seg]?.plural ?? titleCase(seg);
}

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const { overrides } = useBreadcrumbContext();
  const parts = pathname.split("/").filter(Boolean);

  if (parts.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex h-[40px] items-center gap-1.5 border-b border-[var(--border)] bg-white px-6 text-[13px] text-gray-500"
    >
      <Link to="/" className="text-gray-600 hover:text-gray-900">
        Home
      </Link>
      {parts.map((_, idx) => {
        const slice = parts.slice(0, idx + 1).join("/");
        const to = "/" + slice;
        const isLast = idx === parts.length - 1;
        const nonNavigable = NON_NAVIGABLE_PATHS.has(slice);
        const label = resolveLabel(parts, idx, overrides.get(idx));
        return (
          <span key={to} className="flex items-center gap-1.5">
            <ChevronRight size={14} aria-hidden />
            {isLast || nonNavigable ? (
              <span
                className={
                  isLast ? "font-medium text-gray-900" : "text-gray-500"
                }
              >
                {label}
              </span>
            ) : (
              <Link to={to} className="text-gray-600 hover:text-gray-900">
                {label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
